import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import ResendConfirmation from '../../components/auth/ResendConfirmation';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

/**
 * Where the confirmation email lands.
 *
 * The link confirms on arrival, with nothing to click: the address is proven
 * by the link having been opened. It does not sign anybody in — the link may
 * be opened on another device than the one that signed up — and hands over to
 * the sign-in form.
 */
function ConfirmEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState(token ? 'confirming' : 'invalid');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  // A link works once. StrictMode runs effects twice in development, and the
  // second request would be refused and turn a success into an error.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) {
      return;
    }

    sent.current = true;

    const confirm = async () => {
      try {
        const response = await apiCall('/auth/confirm-email', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          const violations = translateViolations(data.violations);
          setError(violations.token || translateApiMessage(data.error) || 'Ce lien n’est plus valable.');
          setState('invalid');
          return;
        }

        setState('confirmed');
      } catch {
        setError('L’adresse n’a pas pu être confirmée. Réessayez.');
        setState('invalid');
      }
    };

    confirm();
  }, [token]);

  const body = () => {
    if ('confirming' === state) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body1">Confirmation en cours…</Typography>
        </Box>
      );
    }

    if ('confirmed' === state) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Alert severity="success">Votre adresse est confirmée. Vous pouvez vous connecter.</Alert>
          <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
            Se connecter
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Alert severity="error">
          {error || 'Ce lien est incomplet. Ouvrez-le directement depuis l’email.'}
        </Alert>
        <Typography variant="body1">
          Entrez l’adresse avec laquelle vous vous êtes inscrit pour recevoir un nouveau lien.
        </Typography>
        <TextField
          label="Adresse email"
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          fullWidth
        />
        <ResendConfirmation email={email} />
      </Box>
    );
  };

  return (
    <PageLayout>
      <Card sx={{ maxWidth: 520, width: '100%', borderRadius: '22px' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Typography variant="h2" component="h1" sx={{ mb: 3 }}>
            Confirmer votre adresse
          </Typography>
          {body()}
        </CardContent>
      </Card>
    </PageLayout>
  );
}

export default ConfirmEmail;
